// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/RecentPointsCard.tsx
import React from 'react';
import type { GamificationEvent, GamificationEventListItem } from '@troia-v3/types';

import { DashCard, StatusPill, type StatusPillVariant } from '@/shared/components/ui';

import { formatRelative } from '../lib/sellerFormat';

import '../seller-dashboard.css';

const RULE_LABEL: Record<GamificationEvent['ruleKey'], string> = {
  lead_captured: 'Lead captado e confirmado',
  form_completed: 'Formulário preenchido',
  lead_won: 'Venda fechada',
};
const STATUS_LABEL: Record<GamificationEvent['status'], string> = {
  pending: 'aguardando WhatsApp', confirmed: 'confirmado', reversed: 'estornado',
};
/** Chip de status = `StatusPill` do DS (âmbar = atenção, verde = confirmado, neutro + riscado = estornado). */
const STATUS_VARIANT: Record<GamificationEvent['status'], StatusPillVariant> = { pending: 'warning', confirmed: 'success', reversed: 'neutral' };

/**
 * "Últimos pontos" — mockup 07: "+3 · Formulário preenchido · Airto José
 * Bona · hoje 17:41 · chip de status".
 *
 * A 2ª linha é `{nome da origem} · {quando}`: o nome vem enriquecido do
 * backend (`GamificationEventListItem.sourceName`, item A da onda final).
 * Quando a origem não pôde ser resolvida (lead apagado, checklist removido),
 * a linha cai só no "quando" — nunca mostra um "·" solto nem "undefined".
 */
export const RecentPointsCard: React.FC<{ events: GamificationEventListItem[] }> = ({ events }) => (
  <DashCard icon="star" title="Últimos pontos" subtitle="De onde vieram" testId="seller-points" noPadding>
    <div className="py-1.5">
      {events.length === 0 && <div className="px-5 py-8 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }} data-testid="seller-points-empty">Seus pontos vão aparecer aqui.</div>}
      {events.slice(0, 6).map((ev) => (
        <div key={ev.id} data-testid={`seller-point-row-${ev.id}`} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center px-4 py-2.5 border-t first:border-t-0" style={{ borderColor: 'var(--theme-card-border)' }}>
          <span className="text-lg font-bold min-w-[40px]" style={{ color: ev.status === 'pending' ? 'var(--theme-warning-fg)' : 'var(--theme-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {ev.points > 0 ? `+${ev.points}` : ev.points}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
              {ev.status === 'pending' && ev.ruleKey === 'lead_captured' ? 'Lead captado' : RULE_LABEL[ev.ruleKey]}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }} data-testid={`seller-point-when-${ev.id}`}>
              {ev.sourceName ? `${ev.sourceName} · ${formatRelative(ev.occurredAt)}` : formatRelative(ev.occurredAt)}
            </div>
          </div>
          <StatusPill variant={STATUS_VARIANT[ev.status]} label={STATUS_LABEL[ev.status]} dot={false} className={ev.status === 'reversed' ? 'line-through' : ''} data-testid={`seller-point-status-${ev.id}`} />
        </div>
      ))}
    </div>
  </DashCard>
);
RecentPointsCard.displayName = 'RecentPointsCard';
